/**
 * CreateSimilarPlaylistModal Component
 * Modal for creating a playlist from similar movies
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMovieToPlaylist, createPlaylist, getSimilarMovies, type Movie } from '../api';
import ModalPortal from './ModalPortal';

interface CreateSimilarPlaylistModalProps {
  sourceMovie: Movie;
  onClose: () => void;
}

const MOVIE_COUNT_OPTIONS = [10, 15, 20, 25];

export default function CreateSimilarPlaylistModal({
  sourceMovie,
  onClose,
}: CreateSimilarPlaylistModalProps) {
  const navigate = useNavigate();
  const [playlistName, setPlaylistName] = useState(
    `Movies like ${sourceMovie.original_title || sourceMovie.title}`
  );
  const [movieCount, setMovieCount] = useState(15);
  const [includeRecommendations, setIncludeRecommendations] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  const handleCreate = async () => {
    if (!playlistName.trim()) {
      setError('Please enter a playlist name');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // 1. Create the playlist
      const playlist = await createPlaylist({ name: playlistName.trim() });

      // 2. Fetch similar movies
      setProgress({ current: 0, total: movieCount });
      const similarData = await getSimilarMovies(sourceMovie.tmdb_id, {
        limit: movieCount,
        includeRecommendations,
      });

      // 3. Add movies to the playlist
      const moviesToAdd = similarData.results.slice(0, movieCount);

      for (let i = 0; i < moviesToAdd.length; i++) {
        setProgress({ current: i + 1, total: moviesToAdd.length });
        try {
          await addMovieToPlaylist(playlist.id, moviesToAdd[i].tmdb_id);
        } catch (err) {
          // Continue even if one movie fails
          console.error(`Failed to add movie ${moviesToAdd[i].title}:`, err);
        }
      }

      // 4. Navigate to the new playlist
      onClose();
      navigate(`/playlists/${playlist.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create playlist');
      setCreating(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !creating) {
      onClose();
    }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal create-similar-playlist-modal">
          <div className="modal-header">
            <h2>Create Similar Playlist</h2>
            {!creating && (
              <button className="modal-close" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="modal-body">
            {/* Source Movie Preview */}
            <div className="source-movie-preview">
              <span className="source-movie-label">Based on</span>
              <div className="source-movie-info">
                {sourceMovie.poster_url && (
                  <img
                    src={sourceMovie.poster_url}
                    alt={sourceMovie.title}
                    className="source-movie-poster"
                  />
                )}
                <div className="source-movie-details">
                  <span className="source-movie-title">
                    {sourceMovie.original_title || sourceMovie.title}
                  </span>
                  {sourceMovie.year && (
                    <span className="source-movie-year">{sourceMovie.year}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Playlist Name */}
            <div className="form-group">
              <label htmlFor="playlist-name">Playlist Name</label>
              <input
                id="playlist-name"
                type="text"
                value={playlistName}
                onChange={(e) => setPlaylistName(e.target.value)}
                disabled={creating}
                placeholder="Enter playlist name..."
              />
            </div>

            {/* Number of Movies */}
            <div className="form-group">
              <label htmlFor="movie-count">Number of movies</label>
              <select
                id="movie-count"
                value={movieCount}
                onChange={(e) => setMovieCount(Number(e.target.value))}
                disabled={creating}
              >
                {MOVIE_COUNT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {count} movies
                  </option>
                ))}
              </select>
            </div>

            {/* Include Recommendations Toggle */}
            <div className="form-group toggle-group">
              <label htmlFor="include-recommendations">
                <span>Include recommendations</span>
                <span className="toggle-hint">
                  Add more variety by including TMDB recommendations
                </span>
              </label>
              <label className="toggle">
                <input
                  id="include-recommendations"
                  type="checkbox"
                  checked={includeRecommendations}
                  onChange={(e) => setIncludeRecommendations(e.target.checked)}
                  disabled={creating}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            {/* Description */}
            <p className="modal-description">
              This will find movies similar to <strong>{sourceMovie.title}</strong> using genre,
              themes, and style.
            </p>

            {/* Error */}
            {error && <div className="modal-error">{error}</div>}

            {/* Progress */}
            {progress && (
              <div className="modal-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
                <span className="progress-text">
                  Adding movies: {progress.current} of {progress.total}
                </span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="secondary" onClick={onClose} disabled={creating}>
              Cancel
            </button>
            <button onClick={handleCreate} disabled={creating || !playlistName.trim()}>
              {creating ? (
                <>
                  <span className="action-spinner" />
                  Creating...
                </>
              ) : (
                'Create Playlist'
              )}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
